import { Stack, styled, Typography } from "@mui/material";
import { black } from "@/theme/colors";
import { CaseTagStatus } from "@/features/case/components/CaseTagStatus";
import { CASE_STATUS, TCaseStatus } from "@/features/case/constants/caseStatus";
import { FCC } from "@/types/common";
import { EMPTY_STR } from "@/constants/string";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { ECaseMatchStatus } from "@/types/enum";
import { Button } from "@/components/elements/Button";
import { IconSocialShare } from "@/assets/icons";
import { CaseShareEnterpriseURLModal } from "@/features/case/components/CaseBasicInfo/CaseShareEnterpriseURLModal";
import { useVisible } from "@/hooks/useVisible";

type CaseTitleProps = {
  jobInfo: TGetJobDto;
  managementNumber?: string;
  caseName?: string;
  progressStatus?: number;
  caseMatches?: TCaseMatches[];
  totalCandidates?: number;
  isAllFailed?: boolean;
  shareUrl?: string;
  sharePassword?: string;
  shareUrlExpiredAt?: string;
  isSharedView?: boolean;
  hideShareButton?: boolean;
};

export const CaseTitle: FCC<CaseTitleProps> = ({
  jobInfo,
  managementNumber,
  caseName,
  progressStatus,
  caseMatches,
  totalCandidates: totalCandidatesProp,
  isAllFailed,
  shareUrl,
  sharePassword,
  shareUrlExpiredAt,
  isSharedView = false,
  hideShareButton = false,
}) => {
  const caseStatus: TCaseStatus =
    progressStatus !== undefined && Object.values(CASE_STATUS).includes(progressStatus as TCaseStatus)
      ? (progressStatus as TCaseStatus)
      : CASE_STATUS.PREPARING_PROPOSAL;

  const totalCandidates = totalCandidatesProp ?? caseMatches?.length ?? 0;
  const offerAcceptedCount =
    caseMatches?.filter((caseMatch) => caseMatch.status === ECaseMatchStatus.OFFER_ACCEPTED || caseMatch.status === ECaseMatchStatus.ALREADY_HIRED)
      .length ?? 0;
  const shareModal = useVisible();
  return (
    <TitleContainer>
      <Stack direction="row" alignContent="end" justifyContent="space-between" gap={2}>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignContent="end" alignItems="end" gap={2} flexWrap="wrap">
            <Typography color={black[30]} variant="head20Bold" sx={{ wordBreak: "break-word", maxWidth: "100%" }}>
              {jobInfo.jobName} - {caseName ?? EMPTY_STR.TEXT}
            </Typography>
            <CaseTagStatus status={caseStatus} caseMatches={caseMatches} isAllFailed={isAllFailed} />
          </Stack>
          <Stack direction="row" alignContent="end" alignItems="end" gap={2} flexWrap="wrap">
            <Typography color={black[70]} variant="body14Light" sx={{ wordBreak: "break-word" }}>
              案件管理番号: {managementNumber ?? EMPTY_STR.TEXT}
            </Typography>
            <Typography color={black[70]} variant="body14Light" sx={{ wordBreak: "break-word" }}>
              企業名：{jobInfo.company?.companyName ?? EMPTY_STR.TEXT}
            </Typography>
            <Typography color={black[70]} variant="body14Light" sx={{ wordBreak: "break-word" }}>
              案件開始日：{dayjs().format(DATE_FORMAT) ?? EMPTY_STR.TEXT}
            </Typography>
            {caseMatches && (
              <Typography color={black[70]} variant="body14Light" sx={{ wordBreak: "break-word" }}>
                候補者: {totalCandidates}名{offerAcceptedCount > 0 && `（うち内定受諾者：${offerAcceptedCount}名）`}
              </Typography>
            )}
          </Stack>
        </Stack>
        {!isSharedView && !hideShareButton && (
          <Button variant="secondary" startIcon={<IconSocialShare />} size="sm" onClick={shareModal.open} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            企業へURL共有
          </Button>
        )}
      </Stack>
      <CaseShareEnterpriseURLModal modal={shareModal} initialShareUrl={shareUrl} initialPassword={sharePassword} initialShareUrlExpiredAt={shareUrlExpiredAt} />
    </TitleContainer>
  );
};

const TitleContainer = styled(Stack)`
  width: 1134px;
  min-height: 68px;
  justify-content: space-between;
  opacity: 1;
`;
